import { apiClient } from './apiClient';
import { supabase } from '@/lib/supabase';
import { isConfirmedOutboundFreshdeskReceipt } from '../../supabase/functions/_shared/freshdesk-receipt-contract';

export class FreshdeskReplyDeliveryReviewError extends Error {
  constructor() {
    super('Freshdesk reply delivery is unconfirmed. Review the ticket conversation before another customer send.');
    this.name = 'FreshdeskReplyDeliveryReviewError';
  }
}

export interface ExtractedPassport {
  number: string;
  expiry_date?: string;
  issue_date?: string;
  issuing_country?: string;
  holder_name?: string;
}

export interface ExtractedTravelDocument {
  type: string;
  number?: string;
  expiry_date?: string;
  country?: string;
}

export interface ExtractedLoyaltyProgram {
  program: string;
  number: string;
  status?: string;
}

export interface ExtractedPaymentPreference {
  type: string;
  details?: string;
  preference_note?: string;
}

export interface ExtractedEmergencyContact {
  name: string;
  phone: string;
  relationship?: string;
}

export interface ExtractedHistoricalTrip {
  pnr_locator?: string;
  booking_reference?: string;
  origin?: string;
  destination?: string;
  route_description?: string;
  departure_date?: string;
  return_date?: string;
  airline?: string;
  flight_numbers?: string[];
  cabin_class?: string;
  passenger_names?: string[];
  passenger_count?: number;
  total_value?: number;
  currency?: string;
  ticket_id?: number;
  trip_purpose?: string;
  notes?: string;
}

export interface ExtractedCustomerData {
  passports: ExtractedPassport[];
  date_of_birth?: string;
  nationality?: string;
  travel_documents: ExtractedTravelDocument[];
  loyalty_programs: ExtractedLoyaltyProgram[];
  payment_preferences: ExtractedPaymentPreference[];
  preferred_airlines: string[];
  cabin_class_preference?: string;
  seat_preference?: string;
  meal_preference?: string;
  dietary_requirements?: string;
  accessibility_needs?: string;
  medical_conditions?: string;
  special_requirements?: string;
  emergency_contact?: ExtractedEmergencyContact;
  travel_patterns?: string;
  common_destinations?: string[];
  notes?: string;
  historical_trips?: ExtractedHistoricalTrip[];
}

export interface FreshdeskContactSummary {
  id: number;
  name: string;
  email: string;
  ticketCount: number;
}

export interface HistoricalTripsResult {
  extracted: number;
  created: number;
  trips: Array<{ id: string; pnr_locator: string }>;
}

export interface EnrichmentResult {
  success: boolean;
  customer?: FreshdeskEnrichmentCustomer;
  freshdeskContact?: FreshdeskContactSummary;
  extractedData?: ExtractedCustomerData;
  historicalTrips?: HistoricalTripsResult;
  processingTimeMs?: number;
  isNewCustomer?: boolean;
  error?: string;
  message?: string;
}

export interface EnrichmentRequest {
  customerId?: string;
  email?: string;
  phone?: string;
  freshdeskContactId?: number;
  forceRefresh?: boolean;
}

export interface SyncCustomerResult {
  customerId: string;
  customerName: string;
  synced: boolean;
  lastSyncedAt: string;
  enrichmentResult: {
    success: boolean;
    ticketCount: number;
    extractedTrips: number;
    createdTrips: number;
    processingTimeMs: number;
  };
}

export interface BatchSyncResult {
  message: string;
  total: number;
  synced: number;
  failed: number;
  skipped: number;
  results: Array<{
    customerId: string;
    customerName: string;
    status: 'success' | 'failed' | 'skipped';
    error?: string;
    ticketCount?: number;
    tripsCreated?: number;
  }>;
}

export interface BackfillImportResult {
  window: {
    days: number;
    mode: 'updated_since';
    cutoff: string;
  };
  scanned: {
    tickets: number;
    unique_requesters: number;
    contacts_fetched: number;
  };
  classification?: {
    customer_request: number;
    supplier_message: number;
    marketing: number;
    spam: number;
    internal: number;
    cached: number;
    errors: number;
    skipped_total: number;
  };
  customers: {
    created: number;
    updated: number;
    skipped: number;
    errors: number;
  };
  suppliers?: {
    suppliers_created: number;
    suppliers_matched: number;
    tickets_routed: number;
    interactions_created: number;
    pnr_links: number;
    customer_links: number;
    errors: number;
  };
  interactions: {
    tickets: { created: number; updated: number; errors: number };
    conversations: { created: number; skipped_existing: number; errors: number };
  };
  enrichment: {
    attempted: number;
    succeeded: number;
    failed: number;
    skipped: number;
    maxEnrichCustomers: number;
  };
  skipped: {
    ticket_missing_updated_at: number;
    ticket_missing_requester_id: number;
    contact_fetch_failed: number;
  };
  errors: Array<{ scope: string; id?: string | number; message: string }>;
  dryRun: boolean;
}

export interface BackfillImportJobResponse {
  runId: string;
  queued: boolean;
  dryRun?: boolean;
  message?: string;
}

interface FreshdeskEnrichmentMetadata {
  last_enriched_at?: string;
  last_synced_at?: string;
  historical_trips_count?: number;
  passports?: ExtractedPassport[];
  travel_documents?: ExtractedTravelDocument[];
  payment_preferences?: ExtractedPaymentPreference[];
  cabin_class_preference?: string;
  travel_patterns?: string;
  common_destinations?: string[];
  notes?: string;
}

interface FreshdeskEnrichmentCustomer {
  freshdesk_id?: number | string | null;
  custom_fields?: {
    freshdesk_enrichment?: FreshdeskEnrichmentMetadata;
  } | null;
  loyalty_programs?: ExtractedLoyaltyProgram[];
  preferred_airlines?: string[];
  seat_preferences?: {
    preferred_seat?: string | null;
  } | null;
  meal_preferences?: {
    meal_type?: string | null;
  } | null;
  dietary_requirements?: string | null;
  accessibility_needs?: string | null;
  medical_conditions?: string | null;
  special_requirements?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  emergency_contact_relationship?: string | null;
}

interface FreshdeskTicketRecord {
  id: number;
  subject?: string;
  created_at?: string;
  requester_email?: string;
  requester_name?: string;
  status?: number;
  url?: string;
  [key: string]: unknown;
}

interface FreshdeskGatewayEnvelope<T> {
  success: boolean;
  data: T;
}

interface CreateTicketGatewayData {
  ticket?: FreshdeskTicketRecord | null;
  ticketId?: number | string;
}

interface SmartSearchResponse {
  bestMatch: {
    ticketId: number;
    score: number;
    signals: string[];
    ticket: {
      id: number;
      subject: string;
      requester_email?: string;
      requester_name?: string;
      status: number;
      created_at: string;
    };
  } | null;
  allMatches: Array<{
    ticketId: number;
    score: number;
    signals: string[];
    subject: string;
  }>;
  queriesAttempted: Array<{ phase?: string; query: string; found: number }>;
  message: string;
}

export interface FreshdeskContactSuggestionResult {
  contactId: number;
  contact: { id: number; name: string | null; email: string | null; phone: string | null };
  score: number;
  signals: string[];
  bestTicket: { id: number; subject: string; created_at: string; url?: string } | null;
  ticketsScanned: number;
}

interface SmartContactSuggestResponse {
  suggestions: FreshdeskContactSuggestionResult[];
  queriesAttempted: Array<{ phase: string; query: string; found: number }>;
  message: string;
}

interface ImportCustomerFromFreshdeskResponse {
  customerId: string;
  resolvedBy: 'ticket' | 'contactId' | 'contactQuery';
  ticketCount: number;
}

interface FreshdeskCustomerIdentityPreviewResponse {
  dryRun: true;
  resolvedBy: 'ticket' | 'contactId' | 'contactQuery';
  contact: {
    id: number;
    name: string | null;
    email: string | null;
    phone: string | null;
  };
  ticketCount: number;
  classificationWarning?: string | null;
}

function unwrapGatewayData<T>(payload: T | FreshdeskGatewayEnvelope<T> | null | undefined): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data;
  }

  return payload as T;
}

export class FreshdeskEnrichmentService {
  /**
   * Enriches a customer profile with data from Freshdesk contact details and ticket history.
   * Uses AI to parse and extract relevant CRM information.
   * 
   * @param params - Either customerId, email, or phone to identify the customer
   * @returns Enriched customer data with extracted information
   */
  async enrichCustomer(params: EnrichmentRequest): Promise<EnrichmentResult> {
    const { data, error } = await apiClient.invoke<EnrichmentResult>('freshdesk-enrich-customer', {
      body: params,
      timeout: 120000, // 2 minutes - AI analysis can take time
    });

    if (error) {
      return {
        success: false,
        error: error.message || 'Failed to enrich customer from Freshdesk',
      };
    }

    return data as EnrichmentResult;
  }

  /**
   * Enriches a customer by their CRM ID
   */
  async enrichById(customerId: string, forceRefresh = false): Promise<EnrichmentResult> {
    return this.enrichCustomer({ customerId, forceRefresh });
  }

  /** Attach a reviewed Freshdesk contact to an existing CRM customer and enrich that row. */
  async enrichByIdWithContact(
    customerId: string,
    freshdeskContactId: number,
    forceRefresh = true,
  ): Promise<EnrichmentResult> {
    return this.enrichCustomer({ customerId, freshdeskContactId, forceRefresh });
  }

  /**
   * Enriches a customer by their email address.
   * If no CRM customer exists, a new one will be created from Freshdesk data.
   */
  async enrichByEmail(email: string, forceRefresh = false): Promise<EnrichmentResult> {
    return this.enrichCustomer({ email, forceRefresh });
  }

  /**
   * Enriches a customer by their phone number.
   * If no CRM customer exists, a new one will be created from Freshdesk data.
   */
  async enrichByPhone(phone: string, forceRefresh = false): Promise<EnrichmentResult> {
    return this.enrichCustomer({ phone, forceRefresh });
  }

  /**
   * Formats the extracted data for display purposes
   */
  formatExtractedDataSummary(data: ExtractedCustomerData): string {
    const parts: string[] = [];

    if (data.passports.length > 0) {
      parts.push(`${data.passports.length} passport(s)`);
    }

    if (data.loyalty_programs.length > 0) {
      const programs = data.loyalty_programs.map(p => p.program).join(', ');
      parts.push(`Loyalty: ${programs}`);
    }

    if (data.preferred_airlines.length > 0) {
      parts.push(`Airlines: ${data.preferred_airlines.join(', ')}`);
    }

    if (data.cabin_class_preference) {
      parts.push(`Class: ${data.cabin_class_preference}`);
    }

    if (data.seat_preference) {
      parts.push(`Seat: ${data.seat_preference}`);
    }

    if (data.travel_documents.length > 0) {
      parts.push(`${data.travel_documents.length} travel doc(s)`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'No data extracted';
  }

  /**
   * Checks if a customer has been enriched from Freshdesk
   */
  hasBeenEnriched(customer: FreshdeskEnrichmentCustomer | null | undefined): boolean {
    return !!(
      customer?.freshdesk_id ||
      customer?.custom_fields?.freshdesk_enrichment?.last_enriched_at
    );
  }

  /**
   * Gets the last enrichment date for a customer
   */
  getLastEnrichmentDate(customer: FreshdeskEnrichmentCustomer | null | undefined): Date | null {
    const enrichedAt = customer?.custom_fields?.freshdesk_enrichment?.last_enriched_at;
    return enrichedAt ? new Date(enrichedAt) : null;
  }

  /**
   * Gets the enrichment data stored in custom_fields
   */
  getEnrichmentData(customer: FreshdeskEnrichmentCustomer | null | undefined): ExtractedCustomerData | null {
    const enrichment = customer?.custom_fields?.freshdesk_enrichment;
    if (!enrichment) return null;

    return {
      passports: enrichment.passports || [],
      travel_documents: enrichment.travel_documents || [],
      loyalty_programs: customer.loyalty_programs || [],
      payment_preferences: enrichment.payment_preferences || [],
      preferred_airlines: customer.preferred_airlines || [],
      cabin_class_preference: enrichment.cabin_class_preference,
      seat_preference: customer.seat_preferences?.preferred_seat ?? undefined,
      meal_preference: customer.meal_preferences?.meal_type ?? undefined,
      dietary_requirements: customer.dietary_requirements ?? undefined,
      accessibility_needs: customer.accessibility_needs ?? undefined,
      medical_conditions: customer.medical_conditions ?? undefined,
      special_requirements: customer.special_requirements ?? undefined,
      emergency_contact: customer.emergency_contact_name && customer.emergency_contact_phone ? {
        name: customer.emergency_contact_name,
        phone: customer.emergency_contact_phone,
        relationship: customer.emergency_contact_relationship ?? undefined,
      } : undefined,
      travel_patterns: enrichment.travel_patterns,
      common_destinations: enrichment.common_destinations || [],
      notes: enrichment.notes,
    };
  }

  /**
   * Gets the last sync date for a customer
   */
  getLastSyncDate(customer: FreshdeskEnrichmentCustomer | null | undefined): Date | null {
    const syncedAt = customer?.custom_fields?.freshdesk_enrichment?.last_synced_at;
    return syncedAt ? new Date(syncedAt) : null;
  }

  /**
   * Gets the count of historical trips extracted from Freshdesk
   */
  getHistoricalTripsCount(customer: FreshdeskEnrichmentCustomer | null | undefined): number {
    return customer?.custom_fields?.freshdesk_enrichment?.historical_trips_count || 0;
  }

  /**
   * Syncs a single customer with Freshdesk - full re-sync including trip extraction
   */
  async syncCustomer(customerId: string, forceRefresh = true): Promise<SyncCustomerResult> {
    const { data, error } = await apiClient.invoke<{ success: boolean; data: SyncCustomerResult }>('freshdesk-gateway', {
      body: {
        action: 'sync-customer',
        params: { customerId, forceRefresh },
      },
      timeout: 120000,
    });

    if (error) {
      throw new Error(error.message || 'Failed to sync customer');
    }

    return unwrapGatewayData<SyncCustomerResult>(data);
  }

  /**
   * Batch sync multiple customers with Freshdesk
   */
  async batchSync(options: {
    limit?: number;
    offset?: number;
    onlyWithFreshdeskId?: boolean;
    forceRefresh?: boolean;
  } = {}): Promise<BatchSyncResult> {
    const { data, error } = await apiClient.invoke<{ success: boolean; data: BatchSyncResult }>('freshdesk-gateway', {
      body: {
        action: 'batch-sync',
        params: {
          limit: options.limit || 50,
          offset: options.offset || 0,
          onlyWithFreshdeskId: options.onlyWithFreshdeskId || false,
          forceRefresh: options.forceRefresh || false,
        },
      },
      timeout: 300000, // 5 minutes for batch operations
    });

    if (error) {
      throw new Error(error.message || 'Failed to batch sync customers');
    }

    return unwrapGatewayData<BatchSyncResult>(data);
  }

  /**
   * Backfill import: scans Freshdesk tickets updated in the last X days, creates/updates customers,
   * ingests ticket + conversation history, and optionally runs enrichment (capped).
   */
  async backfillImport(options: {
    days: number;
    maxTickets?: number;
    includeConversations?: boolean;
    maxConversations?: number;
    enrichWithAi?: boolean;
    maxEnrichCustomers?: number;
    dryRun?: boolean;
    runId?: string;
    forceReclassify?: boolean;
  }): Promise<BackfillImportJobResponse> {
    const { data, error } = await apiClient.invoke<{ success: boolean; data: BackfillImportJobResponse }>('freshdesk-gateway', {
      body: {
        action: 'backfill-import',
        params: {
          days: options.days,
          maxTickets: options.maxTickets ?? 500,
          includeConversations: options.includeConversations ?? true,
          maxConversations: options.maxConversations ?? 50,
          enrichWithAi: options.enrichWithAi ?? true,
          maxEnrichCustomers: options.maxEnrichCustomers ?? 25,
          dryRun: options.dryRun ?? false,
          runId: options.runId,
          forceReclassify: options.forceReclassify ?? false,
        },
      },
      timeout: 300000, // 5 minutes
    });

    if (error) {
      throw new Error(error.message || 'Failed to backfill import from Freshdesk');
    }

    return unwrapGatewayData<BackfillImportJobResponse>(data);
  }
  async createTicket(data: { email: string; subject: string; description: string; priority: number; status: number }) {
    const { data: responseData, error } = await apiClient.invoke<FreshdeskGatewayEnvelope<CreateTicketGatewayData>>('freshdesk-gateway', {
      body: {
        action: 'create-ticket',
        params: data,
      },
      timeout: 60000,
    });

    if (error) {
      throw new Error(error.message || 'Failed to create Freshdesk ticket');
    }

    const result = unwrapGatewayData<CreateTicketGatewayData>(responseData);
    const ticket = result.ticket || null;
    const ticketId = Number(result.ticketId ?? ticket?.id);

    if (!Number.isFinite(ticketId) || ticketId <= 0) {
      throw new Error('Freshdesk gateway did not return a valid ticket id');
    }

    return {
      success: true,
      ticket: {
        ...(ticket || {}),
        id: ticketId,
        subject: ticket?.subject || data.subject,
      },
    };
  }

  /**
   * Search for tickets matching a query (e.g. RLOC)
   */
  async searchTickets(query: string): Promise<{ success: boolean; tickets: FreshdeskTicketRecord[] }> {
    const { data, error } = await apiClient.invoke<FreshdeskGatewayEnvelope<FreshdeskTicketRecord[]>>('freshdesk-gateway', {
      body: {
        action: 'search',
        params: { query: `"${query}"` } // Quote for exact phrase match if possible, or just pass query
      }
    });

    if (error) {
      console.error('FD Search failed', error);
      return { success: false, tickets: [] };
    }

    return { success: true, tickets: unwrapGatewayData<FreshdeskTicketRecord[]>(data) || [] };
  }

  /**
   * Sync/Interact with a specific ticket (update status, reply, or full sync)
   */
  async syncTicket(
    ticketId: number,
    action: 'sync' | 'update_status' | 'post_reply',
    payload: Record<string, unknown> = {}
  ): Promise<unknown> {
    if (action === 'update_status') {
      const { data, error } = await apiClient.invoke<{ success?: boolean; data?: unknown }>('freshdesk-gateway', {
        body: { action: 'update-ticket', params: { ticketId, status: payload.status } },
        retries: 0,
      });
      if (error || data?.success !== true) throw new Error(error?.message || 'Freshdesk status outcome is unconfirmed');
      return data.data;
    }
    if (action !== 'post_reply') {
      const { data, error } = await apiClient.invoke('freshdesk-sync-tickets', {
        body: { action, ticketId, ...payload },
      });
      if (error) throw new Error(error.message || 'Failed to sync ticket');
      return data;
    }

    const { data: claimId, error: claimError } = await supabase.rpc('ops_claim_fd_reply', {
      p_item_key: `crm:fd:${ticketId}`,
      p_ticket_id: ticketId,
    });
    if (claimError || !claimId) throw new FreshdeskReplyDeliveryReviewError();

    try {
      const { data, error } = await apiClient.invoke<{
        success?: boolean;
        data?: { note?: unknown; opsClaimId?: string };
      }>('freshdesk-gateway', {
        body: {
          action: payload.private === true ? 'add-note' : 'reply-ticket',
          params: {
            ...(payload.private === true ? { ticketId, private: true } : { ticket_id: ticketId }),
            body: payload.body, opsClaimId: claimId, commTicketId: payload.commTicketId,
          },
        },
        retries: 0,
      });
      if (error || data?.success !== true || data.data?.opsClaimId !== claimId ||
        !isConfirmedOutboundFreshdeskReceipt(data.data?.note, payload.private === true)) {
        throw error || new Error('Freshdesk returned no confirmed reply receipt');
      }
      const { data: acknowledged, error: acknowledgeError } = await supabase.rpc('ops_ack_fd_reply', {
        p_claim_id: claimId,
      });
      if (acknowledgeError || acknowledged !== true) throw acknowledgeError || new Error('Freshdesk reply acknowledgement unavailable');
      return data.data;
    } catch (error) {
      console.error('[crm] Freshdesk reply requires delivery review', error);
      throw new FreshdeskReplyDeliveryReviewError();
    }
  }

  /**
   * Smart Search - Enhanced PNT matching with cascading queries, nickname variants, and ticket scoring
   * v0.6.1.2: Implements improvements 1-8 from PNT matching enhancement plan
   * 
   * @param context Transaction context for building search queries and scoring tickets
   * @returns Best matching ticket with score and signals, or null if below threshold
   */
  async smartSearch(context: {
    pnrLocator?: string | null;
    lastName?: string | null;
    firstName?: string | null;
    email?: string | null;
    companyName?: string | null;
    stripeDescription?: string | null;
    paymentId?: string | null;
    cardLast4?: string | null;
    amountGBP?: number | null;
    datePaid?: string | null;
    minScore?: number;
    debug?: boolean;
  }): Promise<{
    success: boolean;
    bestMatch: {
      ticketId: number;
      score: number;
      signals: string[];
      ticket: {
        id: number;
        subject: string;
        requester_email?: string;
        requester_name?: string;
        status: number;
        created_at: string;
      };
    } | null;
    allMatches: Array<{
      ticketId: number;
      score: number;
      signals: string[];
      subject: string;
    }>;
    queriesAttempted: Array<{ phase?: string; query: string; found: number }>;
    message: string;
  }> {
    const { data, error } = await apiClient.invoke<FreshdeskGatewayEnvelope<SmartSearchResponse>>('freshdesk-gateway', {
      body: {
        action: 'smart-search',
        params: {
          pnrLocator: context.pnrLocator,
          lastName: context.lastName,
          firstName: context.firstName,
          email: context.email,
          companyName: context.companyName,
          stripeDescription: context.stripeDescription,
          paymentId: context.paymentId,
          cardLast4: context.cardLast4,
          amountGBP: context.amountGBP,
          datePaid: context.datePaid,
          minScore: context.minScore ?? 40,
          debug: context.debug ?? false
        }
      }
    });

    if (error) {
      console.error('[SmartSearch] Service error:', error);
      return {
        success: false,
        bestMatch: null,
        allMatches: [],
        queriesAttempted: [],
        message: error.message || 'Smart search failed'
      };
    }

    const result = unwrapGatewayData<SmartSearchResponse>(data);
    return {
      success: true,
      bestMatch: result.bestMatch,
      allMatches: result.allMatches || [],
      queriesAttempted: result.queriesAttempted || [],
      message: result.message || 'Search complete'
    };
  }

  /**
   * CRM fallback: suggest Freshdesk contacts by scoring their ticket context.
   * This does NOT require AI; it uses deterministic heuristics + conversation scanning.
   */
  async smartContactSuggest(params: {
    query: string;
    email?: string | null;
    phone?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    companyName?: string | null;
    maxContacts?: number;
    maxTicketsPerContact?: number;
    maxDeepTickets?: number;
  }): Promise<{
    suggestions: FreshdeskContactSuggestionResult[];
    queriesAttempted: Array<{ phase: string; query: string; found: number }>;
    message: string;
  }> {
    const { data, error } = await apiClient.invoke<FreshdeskGatewayEnvelope<SmartContactSuggestResponse>>('freshdesk-gateway', {
      body: {
        action: 'smart-contact-suggest',
        params,
      },
      timeout: 60000,
    });

    if (error) {
      return {
        suggestions: [],
        queriesAttempted: [],
        message: error.message || 'Freshdesk suggestion failed',
      };
    }

    const result = unwrapGatewayData<SmartContactSuggestResponse>(data);
    return {
      suggestions: result.suggestions || [],
      queriesAttempted: result.queriesAttempted || [],
      message: result.message || 'Suggestion complete',
    };
  }

  /**
   * Resolve the Freshdesk requester without creating/updating a CRM customer.
   * PNT uses this inspection step to quarantine contaminated requester emails
   * before invoking the import path.
   */
  async previewCustomerIdentityFromFreshdesk(ticketId: number): Promise<FreshdeskCustomerIdentityPreviewResponse> {
    const { data, error } = await apiClient.invoke<FreshdeskGatewayEnvelope<FreshdeskCustomerIdentityPreviewResponse>>('freshdesk-gateway', {
      body: {
        action: 'import-customer',
        params: {
          ticketId,
          dryRun: true,
          // This is an identity-only read. dryRun prevents mutation while
          // forceImport skips classification/AI work before returning contact.
          forceImport: true,
          options: {
            includeConversations: false,
            maxTickets: 1,
            maxConversations: 0,
            enrichWithAi: false,
          },
        },
      },
      timeout: 120000,
    });

    if (error) throw new Error(error.message || 'Freshdesk identity preview failed');
    const result = unwrapGatewayData<FreshdeskCustomerIdentityPreviewResponse>(data);
    if (!result?.contact?.id) throw new Error('Freshdesk identity preview did not return a requester contact');
    return result;
  }

  /**
   * Import/create a CRM customer from Freshdesk contact (or ticket) via gateway.
   * This ingests ticket history and optionally runs enrichment (AI if configured).
   */
  async importCustomerFromFreshdesk(params: {
    ticketId?: number;
    contactId?: number;
    contactQuery?: string;
    forceImport?: boolean;
    options?: {
      includeConversations?: boolean;
      maxTickets?: number;
      maxConversations?: number;
      enrichWithAi?: boolean;
      confidenceThreshold?: number;
    };
  }): Promise<{
    customerId: string;
    resolvedBy: 'ticket' | 'contactId' | 'contactQuery';
    ticketCount: number;
  }> {
    const { data, error } = await apiClient.invoke<FreshdeskGatewayEnvelope<ImportCustomerFromFreshdeskResponse>>('freshdesk-gateway', {
      body: {
        action: 'import-customer',
        params,
      },
      timeout: 120000,
    });

    if (error) {
      throw new Error(error.message || 'Freshdesk import failed');
    }

    const result = unwrapGatewayData<ImportCustomerFromFreshdeskResponse>(data);
    return {
      customerId: String(result.customerId),
      resolvedBy: result.resolvedBy,
      ticketCount: Number(result.ticketCount || 0),
    };
  }
}

export const freshdeskEnrichmentService = new FreshdeskEnrichmentService();
